import {Capacitor} from "@capacitor/core";
import {QueryClient} from "@tanstack/react-query";
import {config} from "./config/config";
import {announcePassGate} from "./pricing/passGateBus";
import {reportApiFailure} from "./utilities/errorReporter";

export function apiCall(path: apiMethods | apiGetMethods, params = {}, initConfig: any = {}) {
    const platform = (config.isCordova ? (Capacitor.getPlatform() || 'mobile').toLowerCase() : 'web');

    return fetch(config.apiUrl + '/' + String(path) + (typeof document !== 'undefined' && document.cookie.indexOf('XDEBUG_SESSION') > -1 ? config.debugParams : ''), {
        method: "POST",
        body:   JSON.stringify(params),

        ...initConfig,

        headers: {
            ...(initConfig.hasOwnProperty('body') ? {} : {'Content-Type': 'application/json'}), // custom body format
            'Accept':          'application/json',
            'x-firebase-auth': api.user?.['stsTokenManager']?.accessToken,
            'x-jwt-token':     api.jwt_token,
            'x-user-info':     (api.user?.uid || '-') + ':' + config.version + ':' + platform, // logged in access log

            ...(initConfig.headers || {}),
        },
    }).then(
        (response) => {
            // Server-side failures feed the friction log (Admin → Client Errors); 4xx are
            // application-level answers (validation, caps) and stay out of it.
            if (response.status >= 500) reportApiFailure(String(path), response.status);
            return response;
        },
        (err) => {
            reportApiFailure(String(path), 0, err?.message || String(err)); // network-level failure
            throw err;
        },
    );
}

const api = {
    jwt_token: '',
    user:      undefined as any,
}

type apiMethods =
    'football-players-create' | 'football-games-create' | 'football-social-games-delete' | 'football-social-settings' | 'club-players-merge' | 'football-games-roster-set' | 'football-games-stats-set' | 'team-join-link' | 'team-join' |
    'contact'
    | 'delete-account'
    | 'applications-create'

    | 'tournaments-create'
    | 'tournaments-update'
    | 'tournaments-generate-schedule'
    | 'tournaments-send-payment-emails'
    | 'tournaments-delete'
    | 'tournaments-schedule-create'
    | 'tournaments-schedule-clear'
    | 'tournaments-complete'

    | 'leagues-create'
    | 'leagues-season-create'
    | 'leagues-season-preview'
    | 'leagues-season-create-next'
    | 'leagues-update'
    | 'leagues-send-payment-emails'
    | 'leagues-delete'
    | 'league-participants-create'
    | 'league-participants-update'
    | 'league-participants-delete'
    | 'leagues-challenge-create'
    | 'leagues-challenge-accept'
    | 'leagues-challenge-decline'
    | 'leagues-set-positions'
    | 'league-transfer-ownership'
    | 'tournament-transfer-ownership'

    | 'americanos-create'
    | 'americanos-submit-score'

    | 'players-create'
    | 'players-create-and-add'
    | 'players-import'
    | 'event-pass/status'
    | 'event-pass/checkout'
    | 'event-pass/confirm'
    | 'event-pass/apply'
    | 'players-update'
    | 'players-delete'

    | 'games-create'
    | 'games-update'
    | 'games-delete'
    | 'games-invite'

    | 'posts-create'
    | 'posts-update'
    | 'posts-delete'

    | 'teams-create'
    | 'teams-update'
    | 'teams-delete'
    | 'leagues-team-replace'
    | 'teams-search'
    | 'club-managers-list'
    | 'club-managers-preview'
    | 'club-managers-invite'
    | 'club-managers-accept'
    | 'club-managers-revoke'
    | 'team-players-create'
    | 'team-players-update'
    | 'team-players-delete'
    | 'team-players-unlink'
    | 'team-players-claim-link'
    | 'club-player-invite-whatsapp'

    | 'auth/me'
    | 'auth/refresh'
    | 'auth/login'
    | 'auth/logout'
    | 'auth/makeSureUserExists'
    | 'auth/roleToggle'
    | 'auth/passwordResetLink'
    | 'auth/passwordResetConfirm'

    | 'signup'
    | 'confirm-email'
    | 'confirm-email-resend'
    | 'phone/send-otp'
    | 'phone/verify-otp'
    | 'users-update'
    | 'players-search'
    | 'geo-search'
    | 'event-apply'
    | 'event-register-guest'
    | 'partner-seeker-join'
    | 'partner-seeker-leave'
    | 'leagues-invite'
    | 'applications-update'
    | 'applications-delete'
    | 'league-change-status'
    | 'league-schedule-create'
    | 'league-schedule-delete'

    | 'ranking-groups-update'
    | 'ranking-groups-delete'
    | 'ranking-groups-create'

    | 'courts-update'
    | 'courts-delete'
    | 'courts-create'
    | 'booking-book'
    | 'booking-quote'
    | 'booking-update'
    | 'booking-delete'
    | 'booking-block'
    | 'booking-cancel'
    | 'booking-cancel-preview'
    | 'booking-ics'
    | 'booking-settings-save'

    | 'tournaments-invite'
    | 'tournament-change-status'
    | 'tournament-schedule-create'
    | 'tournament-schedule-delete'
    | 'tournament-draw-create'
    | 'tournament-draw-delete'
    | 'tournament-stage-confirm'

    | 'submit-questionnaire'
    | 'sponsors-upload'
    | 'banner-crop'
    | 'tournament-teams-create'

    | 'articles-create'
    | 'articles-delete'

    | 'stripe-prepare-payment'
    | 'stripe-prepare-cup-clash-payment'
    | 'stripe-check-cup-clash-payment'
    | 'stripe-subscribe-events'
    | 'messages-create-test'
    | 'submit-feature-request'
    | 'invite-player-by-email'
    | 'players-merge'
    | 'profile-claim'
    | 'invite-claim'
    | 'clubs-players-invite'
    | 'clubs-update'
    | 'clubs-create'
    | 'clubs-live-view-save'
    | 'club-custom-rewards-create'
    | 'club-custom-rewards-update'
    | 'club-custom-rewards-delete'
    | 'communities-update'
    | 'communities-create'
    | 'in-app/register-subscription'
    | 'referrals-invite-by-email'
    | 'wallet-withdraw-manually'

    // Marketplace (MM) checkout
    | 'marketplace-start-checkout'
    | 'marketplace-checkout-status'
    | 'marketplace-join-checkout'
    | 'marketplace-check-in'
    | 'bookings-invite-share-details'
    | 'bookings-invite-share-checkout'
    | 'bookings-cancel-with-refund'
    | 'games-cancel-with-refund'

    | 'slugs-check'
    | 'slugs-suggest'

    // Club subscription
    | 'club-subscription/info'
    | 'club-subscription/checkout'
    | 'club-subscription/success'
    | 'club-subscription/billing-portal'
    ;
type apiGetMethods =
    'football-players-search' | 'football-games-roster' | 'football-social-games' | 'football-social-games-show' | 'football-social-table' | 'football-social-stats' | 'football-player-profile' | 'team-join-show' |
    'tournaments-get'
    | 'master/client-errors'
    | 'tournaments-find'
    | 'tournaments-roundup'
    | 'game-fees-setup'
    | 'game-fees-ledger'
    | 'game-fees-league-outstanding'
    | 'game-fees-mark-paid'
    | 'game-fees-pay-details'
    | 'game-fees-pay-checkout'
    | 'tournaments-ranking'
    | 'tournaments-players-unassigned'

    | 'leagues-find'
    | 'leagues-get'
    | 'leagues-teams'
    | 'leagues-players-unassigned'
    | 'leagues-stats'
    | 'leagues-season-stats'
    | 'leagues-ranking'
    | 'league-participants-get'
    | 'league-participants-find'

    | 'americanos-leaderboard'
    | 'americanos-current-round'
    | 'americanos-round-games'
    | 'americanos-player-stats'

    | 'players-get'
    | 'players-find'
    | 'games-get'
    | 'games-find'
    | 'applications-get'
    | 'teams-get'
    | 'teams-mine'
    | 'team-players-get'
    | 'teams-find'
    | 'teams-update'
    | 'teams-delete'
    | 'messages-get'

    | 'ranking-groups-get'
    | 'ranking-groups-find'

    | 'courts-get'
    | 'courts-find'
    | 'booking-available-courts'
    | 'booking-get'
    | 'booking-monthly-availability'
    | 'booking-start-times'
    | 'booking-clubs'
    | 'booking-calendar'
    | 'booking-settings-get'
    | 'booking-price-preview'

    | 'player-profile'
    | 'organizer-profile'
    | 'public-player-profile'

    | 'users-get'
    | 'users-find'
    | 'tournament-teams'
    | 'tournament-stages'

    | 'marketplace-offers'
    | 'articles-get'
    | 'posts-get'
    | 'posts-find'
    | 'public-blog'
    | 'event-public'
    | 'event-public-list'
    | 'player-schedule'
    | 'subscriptions-get'
    | 'upcoming-games'
    | 'clubs-get'
    | 'clubs-find'
    | 'clubs-live-view'
    | 'club-custom-rewards-get'
    | 'communities-get'
    | 'communities-find'
    | 'global-ranking-league-stats'
    | 'pricing-plans'
    | 'players-merge'
    | 'socialite/google/url'
    | 'socialite/discord/url'
    | 'admin-referrals-get'
    | 'referrer-find'
    | 'referral-stats'
    | 'referrals-get'
    | 'referral-payout-account-url'
    | 'referral-payout-account-status'
    | 'wallet-balance'
    | 'wallet-stripe-dashboard-url'
    ;

/**
 * @deprecated
 */
export function apiRetrieve(path: apiGetMethods, params = {}, initConfig = {}) {
    return apiCall(path, params, initConfig).then((response) => {
        if (response.status >= 400 && response.status !== 401) {
            return response.json().then((obj) => {
                obj.error = obj.message;
                announcePassGate(obj); // mostly reads, but a few old screens write through here too
                return Promise.resolve(obj); // TODO: use Promise.reject(obj)
            })
        } else {
            return response.json();
        }
    });
}

/**
 * Fetch a LIST endpoint, failing loudly on an error response.
 *
 * `apiRetrieve` resolves the error body as though it were data — every status >= 400, and 401 too
 * (it skips the `.error` branch and still returns the parsed body). React Query therefore caches
 * `{message: "..."}` as a SUCCESSFUL result, and the next `.map()`/`.filter()` on it takes the
 * screen down with "(x.data || []).map is not a function". Worse, several screens share one query
 * key (e.g. tournament_teams is read by Setup → Teams and written by Draws), so a single unguarded
 * caller poisons the cache for the guarded ones.
 *
 * Throwing instead puts the query in its error state, so React Query keeps the last good data and
 * the page renders an error rather than a white screen.
 */
export async function apiRetrieveList<T = any>(path: apiGetMethods, params = {}, what = 'data'): Promise<T[]> {
    const r: any = await apiRetrieve(path, params);
    if (!Array.isArray(r)) {
        throw new Error(r?.error || r?.message || `Failed to load ${what}`);
    }
    return r as T[];
}

export function apiRetrieveImproved(path: apiGetMethods, params = {}, initConfig = {}) {
    return apiCall(path, params, initConfig).then((response) => {
        if (response.status >= 400) {
            return response.json().then((obj) => {
                announcePassGate(obj); // the Event Pass offer opens from the app shell — see apiAction
                return Promise.reject(obj);
            })
        } else {
            return response.json();
        }
    });
}

/**
 * @deprecated
 */
export function apiAction(path: apiMethods, params: any = {}, initConfig: any = {}) {
    if (path.endsWith('-update') && !params['values']?.['version'] && !params['version']) {
        throw new Error('Error: version not set on the model');
    }
    return apiCall(path, params, initConfig).then((response) => {
        if (response.status >= 400 && response.status !== 401) {
            return response.json().then((obj) => {
                obj.error = obj.message;
                // The one-off Event Pass gate opens its offer from the app shell, every time,
                // whichever screen made the request (see pricing/passGateBus.ts).
                announcePassGate(obj);
                return Promise.resolve(obj);
            })
        } else {
            return response.json();
        }
    });
}

const inFlightActions = new Map<string, Promise<any>>();

export function dedupedApiAction(path: apiMethods, params: any = {}, initConfig: any = {}) {
    const key = path + '|' + JSON.stringify(params);
    let promise = inFlightActions.get(key);
    if (!promise) {
        promise = apiActionImproved(path, params, initConfig);
        inFlightActions.set(key, promise);
        promise.finally(() => {
            if (inFlightActions.get(key) === promise) inFlightActions.delete(key);
        });
    }
    return promise;
}

export function apiActionImproved(path: apiMethods, params: any = {}, initConfig: any = {}) {
    if (path.endsWith('-update') && !params['values']?.['version'] && !params['version']) {
        throw new Error('Error: version not set on the model');
    }
    return apiCall(path, params, initConfig).then((response) => {
        if (response.status >= 400 && response.status !== 401) {
            return response.json().then((obj) => {
                announcePassGate(obj); // the Event Pass offer opens from the app shell — see apiAction
                return Promise.reject(obj);
            })
        } else {
            return response.json();
        }
    });
}

export function updateQueryDataItem(queryClient: QueryClient, queryKey: any, values: any) {
    queryClient.setQueryData(queryKey, (data: any) => {
        if (!data) {
            console.debug('[updateQueryData] ', JSON.stringify(queryKey), ` not found`);
            return data;
        }
        return {...data, ...values};
    });
}

export function updateQueryData(queryClient: QueryClient, queryKey: any, uid: string, values: any) {
    queryClient.setQueryData(queryKey, (data: any) => {
        if (!data) {
            return data;
        }
        if (data.uid === uid) {
            return {...data, ...values};
        }
        return data.map((oldData: any) => {
            if (oldData.uid === uid) {
                console.debug('[updateQueryData] ', JSON.stringify(queryKey), `[uid=${uid}] :=`, JSON.stringify(values));
                return {
                    ...oldData,
                    ...values,
                };
            }
            return oldData;
        });
    });
}

export default api;
